import { PropertyType, PropertySubtype } from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type { PropertyClassificationOption } from '../types/property-classification.types';

const typeLabels: Record<PropertyType, TranslationKey> = {
  HOUSE: 'catalog.properties.propertyType.HOUSE',
  APARTMENT: 'catalog.properties.propertyType.APARTMENT',
  OFFICE: 'catalog.properties.propertyType.OFFICE',
  RETAIL: 'catalog.properties.propertyType.RETAIL',
  WAREHOUSE: 'catalog.properties.propertyType.WAREHOUSE',
  LAND: 'catalog.properties.propertyType.LAND',
  PARCEL: 'catalog.properties.propertyType.PARCEL',
  PARKING: 'catalog.properties.propertyType.PARKING',
  INDUSTRIAL: 'catalog.properties.propertyType.INDUSTRIAL',
  OTHER: 'catalog.properties.propertyType.OTHER',
};

const subtypePresentations = {
  STUDIO: {
    propertyType: PropertyType.APARTMENT,
    propertySubtype: PropertySubtype.STUDIO,
    labelKey: 'catalog.properties.propertyType.APARTMENT_STUDIO',
  },
} satisfies Record<
  PropertySubtype,
  Pick<
    PropertyClassificationOption,
    'propertyType' | 'propertySubtype' | 'labelKey'
  >
>;

/** Combined options, placing each permitted subtype immediately after its type. */
export const propertyClassificationOptions: readonly PropertyClassificationOption[] =
  [
    { value: '', labelKey: null, propertyType: null, propertySubtype: null },
    ...Object.values(PropertyType).flatMap<PropertyClassificationOption>(
      (propertyType) => [
        {
          value: propertyType,
          labelKey: typeLabels[propertyType],
          propertyType,
          propertySubtype: null,
        },
        ...Object.values(subtypePresentations)
          .filter((presentation) => presentation.propertyType === propertyType)
          .map((presentation) => ({
            value: `${propertyType}_${presentation.propertySubtype}`,
            ...presentation,
          })),
      ]
    ),
  ];

/**
 * Resolves a supported selection, falling back to its base type for stale form data.
 * @param propertyType Current form or API type.
 * @param propertySubtype Current form or API subtype.
 * @returns Matching option, or the empty option for an unknown type.
 */
export function getPropertyClassificationOption(
  propertyType?: string | null,
  propertySubtype?: string | null
): PropertyClassificationOption {
  return (
    propertyClassificationOptions.find(
      (option) =>
        option.propertyType === propertyType &&
        option.propertySubtype === (propertySubtype ?? null)
    ) ??
    propertyClassificationOptions.find(
      (option) =>
        option.propertyType === propertyType && option.propertySubtype === null
    ) ??
    propertyClassificationOptions[0]
  );
}

/**
 * Decodes a combined selector value without subtype-specific UI branches.
 * @param value Value chosen from the classification options.
 * @returns Canonical form values, or the empty option for an unknown value.
 */
export function getPropertyClassificationSelection(
  value: string
): PropertyClassificationOption {
  return (
    propertyClassificationOptions.find((option) => option.value === value) ??
    propertyClassificationOptions[0]
  );
}

/**
 * Formats the same classification label used by the editor on read-only views.
 * @param propertyType Canonical property type.
 * @param propertySubtype Canonical property subtype.
 * @param translate Current locale's translation function.
 * @returns Localized classification label, or a dash for an unclassified property.
 */
export function getPropertyClassificationLabel(
  propertyType: string | null | undefined,
  propertySubtype: string | null | undefined,
  translate: (key: TranslationKey) => string
): string {
  const { labelKey } = getPropertyClassificationOption(
    propertyType,
    propertySubtype
  );
  return labelKey ? translate(labelKey) : '—';
}
